import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import constant from '../constant';
import { getSessionStorage } from '../utils';

const MessageContext = createContext();
const MAX_BUFFERED_MESSAGE_EVENTS = 300;
const MAX_FLOATING_CHAT_WINDOWS = 8;

const normalizeUserId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getCurrentUserIdFromSession = () => {
  if (typeof window === 'undefined') return null;

  try {
    const encoded = getSessionStorage(constant.keys.userDetails);
    if (!encoded) return null;
    const parsed = JSON.parse(atob(encoded));
    const id = Number(parsed?.id);
    return Number.isFinite(id) ? id : null;
  } catch (error) {
    return null;
  }
};

export const MessageProvider = ({ children }) => {
  const [newMessages, setNewMessages] = useState([]);
  const [unreadByUser, setUnreadByUser] = useState({});
  const [floatingChatWindows, setFloatingChatWindows] = useState([]);
  const [activeChatUserId, setActiveChatUserId] = useState(null);
  const seenMessageIdsRef = useRef(new Set());

  const clearUnreadForUser = useCallback((userId) => {
    const normalized = normalizeUserId(userId);
    if (!normalized) return;

    setUnreadByUser((prev) => {
      const key = String(normalized);
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const openFloatingChatWindow = useCallback((chatUser = {}) => {
    const normalized = normalizeUserId(chatUser?.USER_ID ?? chatUser?.userId ?? chatUser?.from);
    if (!normalized) return;

    const displayName = chatUser?.FULL_NAME
      || chatUser?.fullName
      || chatUser?.fromName
      || chatUser?.name
      || `User ${normalized}`;

    const normalizedUser = {
      userId: normalized,
      fullName: displayName,
      profileImageUrl: chatUser?.PROFILE_IMAGE_URL || chatUser?.profileImageUrl || '',
      minimized: Boolean(chatUser?.minimized),
      updatedAt: new Date().toISOString(),
    };

    setFloatingChatWindows((prev) => {
      const existingIndex = prev.findIndex((item) => item.userId === normalized);
      if (existingIndex >= 0) {
        const existing = prev[existingIndex];
        const nextItem = {
          ...existing,
          ...normalizedUser,
          minimized: chatUser?.minimized ?? false,
          updatedAt: new Date().toISOString(),
        };
        return [...prev.slice(0, existingIndex), ...prev.slice(existingIndex + 1), nextItem];
      }

      const next = [...prev, normalizedUser];
      return next.slice(-MAX_FLOATING_CHAT_WINDOWS);
    });
  }, []);

  const closeFloatingChatWindow = useCallback((userId) => {
    const normalized = normalizeUserId(userId);
    if (!normalized) return;
    setFloatingChatWindows((prev) => prev.filter((item) => item.userId !== normalized));
  }, []);

  const setFloatingChatMinimized = useCallback((userId, minimized = false) => {
    const normalized = normalizeUserId(userId);
    if (!normalized) return;

    setFloatingChatWindows((prev) => prev.map((item) => (
      item.userId === normalized ? { ...item, minimized: Boolean(minimized), updatedAt: new Date().toISOString() } : item
    )));
  }, []);

  const toggleFloatingChatMinimize = useCallback((userId) => {
    const normalized = normalizeUserId(userId);
    if (!normalized) return;

    setFloatingChatWindows((prev) => prev.map((item) => (
      item.userId === normalized
        ? { ...item, minimized: !item.minimized, updatedAt: new Date().toISOString() }
        : item
    )));
  }, []);

  const pushNewMessage = useCallback((message) => {
    if (!message) return;

    const incomingId = message?.messageData?.id;
    const incomingIdKey = incomingId ? String(incomingId) : null;

    if (incomingIdKey && seenMessageIdsRef.current.has(incomingIdKey)) {
      return;
    }
    if (incomingIdKey) {
      seenMessageIdsRef.current.add(incomingIdKey);
      if (seenMessageIdsRef.current.size > MAX_BUFFERED_MESSAGE_EVENTS * 4) {
        const keepSize = MAX_BUFFERED_MESSAGE_EVENTS * 2;
        const trimmed = Array.from(seenMessageIdsRef.current).slice(-keepSize);
        seenMessageIdsRef.current = new Set(trimmed);
      }
    }

    setNewMessages((prev) => {
      const isDuplicate = incomingId
        ? prev.some((item) => String(item?.messageData?.id) === String(incomingId))
        : false;
      if (isDuplicate) return prev;

      const next = [...prev, message];
      return next.slice(-MAX_BUFFERED_MESSAGE_EVENTS);
    });

    const senderId = normalizeUserId(
      message?.from
      ?? message?.senderId
      ?? message?.messageData?.from_user_id
      ?? message?.messageData?.fromUserId
      ?? message?.messageData?.senderId
      ?? message?.messageData?.from
      ?? message?.messageData?.from_user
    );
    const currentUserId = getCurrentUserIdFromSession();
    const shouldTrackUnread = senderId
      && senderId !== currentUserId
      && senderId !== activeChatUserId;

    if (shouldTrackUnread) {
      setUnreadByUser((prev) => {
        const key = String(senderId);
        return {
          ...prev,
          [key]: (prev[key] || 0) + 1,
        };
      });

      openFloatingChatWindow({
        userId: senderId,
        fullName: message?.fromName || message?.messageData?.from_user || `User ${senderId}`,
        minimized: false,
      });
    }
  }, [activeChatUserId, openFloatingChatWindow]);

  const setActiveChatUser = useCallback((userId) => {
    const normalized = normalizeUserId(userId);
    setActiveChatUserId(normalized);
    if (normalized) {
      clearUnreadForUser(normalized);
    }
  }, [clearUnreadForUser]);

  const getUnreadCountForUser = useCallback((userId) => {
    const normalized = normalizeUserId(userId);
    if (!normalized) return 0;
    return unreadByUser[String(normalized)] || 0;
  }, [unreadByUser]);

  const totalUnreadCount = useMemo(() => {
    return Object.values(unreadByUser).reduce((sum, count) => sum + Number(count || 0), 0);
  }, [unreadByUser]);

  return (
    <MessageContext.Provider
      value={{
        newMessages,
        pushNewMessage,
        unreadByUser,
        getUnreadCountForUser,
        totalUnreadCount,
        clearUnreadForUser,
        setActiveChatUser,
        floatingChatWindows,
        openFloatingChatWindow,
        closeFloatingChatWindow,
        setFloatingChatMinimized,
        toggleFloatingChatMinimize,
      }}
    >
      {children}
    </MessageContext.Provider>
  );
};

export const useMessageContext = () => useContext(MessageContext);
